import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/apiClient";
import { Emblem } from "@/components/Logo";
import {
  LayoutDashboard, Boxes, ListChecks, HandHeart, CalendarDays, Cake, Trophy,
  GraduationCap, Contact, MessageCircle, Target, FileText, Medal, Star,
  ChevronLeft, ChevronRight, X, Rocket, Home, Sparkles,
} from "lucide-react";

const MEMBER_ROLES = ["membro", "visitante"];

const INTRO_LEADER = {
  icon: Sparkles, cover: true,
  title: "Bem-vindo(a) à Gestão de PGDs!",
  body: "Em poucos passos vamos te mostrar cada parte da plataforma — simples e rápido. Você pode pular quando quiser.",
};
const INTRO_MEMBER = {
  icon: Sparkles, cover: true,
  title: "Que bom ter você aqui! 🙌",
  body: "Vamos te mostrar rapidinho o que você encontra na plataforma da Aprisco. Leva menos de 1 minuto.",
};

const LEADER_STEPS = [
  { icon: LayoutDashboard, title: "Painel", where: "Menu · Painel", body: "A visão do dia ao entrar: PGDs ativos, membros, frequência e avisos do que precisa de atenção." },
  { icon: Boxes, title: "Grupos & Líderes", where: "Menu · Grupos & Líderes", body: "Cadastre e gerencie os PGDs: líderes, dia, horário, endereço, datas e a árvore de multiplicação." },
  { icon: ListChecks, title: "Frequência", where: "Menu · Frequência", body: "Registre a presença de cada encontro, visitantes e a oferta. Acompanhe o histórico semana a semana." },
  { icon: HandHeart, title: "Pedidos de Oração", where: "Menu · Pedidos de Oração", body: "Receba e acompanhe os pedidos de oração do seu grupo e da comunidade." },
  { icon: CalendarDays, title: "Calendário", where: "Menu · Calendário", body: "Reuniões, capacitações e celebrações em um só lugar — próximos eventos e histórico." },
  { icon: Cake, title: "Aniversariantes", where: "Menu · Aniversariantes", body: "Veja quem faz aniversário e envie os parabéns pelo WhatsApp com um toque." },
  { icon: Trophy, title: "Celebração", where: "Menu · Celebração", body: "Reconheça conquistas e marcos importantes dos grupos e da liderança." },
  { icon: GraduationCap, title: "Estudos & Formação", where: "Menu · Estudos & Formação", body: "Estudos do mês e Escola de Liderança, com acompanhamento do seu progresso." },
  { icon: Contact, title: "Contatos", where: "Menu · Contatos", body: "Cadastro de interessados e participantes, já vinculados ao grupo certo." },
  { icon: MessageCircle, title: "Atendimento", where: "Menu · Atendimento", body: "Fale com os membros pelo WhatsApp usando modelos de mensagem prontos e editáveis." },
  { icon: Target, title: "Metas & Compromissos", where: "Menu · Metas & Compromissos", body: "Acompanhe o que foi combinado: lista e Kanban, diário de andamento, lembretes e recorrência." },
  { icon: FileText, title: "Relatório Pastoral", where: "Menu · Relatório Pastoral", body: "Gere relatórios em PDF do supervisor e da liderança, prontos para imprimir e arquivar." },
  { icon: Medal, title: "Certificados", where: "Menu · Certificados", body: "Emita certificados para quem concluiu etapas da formação." },
  { icon: Star, title: "Pesquisas", where: "Menu · Pesquisas", body: "Crie e aplique pesquisas rápidas para ouvir a comunidade." },
];
const LEADER_CTA = {
  icon: ListChecks, cover: true, cta: { label: "Registrar frequência", to: "/frequencia" },
  title: "Pronto para começar! ✨",
  body: "Você já conhece a plataforma. Que tal começar registrando a frequência do seu próximo encontro?",
};

const MEMBER_STEPS = [
  { icon: Boxes, title: "Grupos & Líderes", where: "Menu · Grupos & Líderes", body: "Encontre o seu PGD, veja o endereço e o bairro e abra a localização no mapa." },
  { icon: HandHeart, title: "Pedidos de Oração", where: "Menu · Pedidos de Oração", body: "Envie o seu pedido de oração e acompanhe os pedidos da comunidade." },
  { icon: Cake, title: "Aniversariantes", where: "Menu · Aniversariantes", body: "Veja quem faz aniversário e celebre junto com a família Aprisco." },
  { icon: Home, title: "Página inicial & Suporte", where: "Site público · apriscochurch.app", body: "Na página pública qualquer pessoa encontra um PGD e fala conosco pelo botão de suporte no WhatsApp." },
];
const MEMBER_CTA = {
  icon: Boxes, cover: true, cta: { label: "Encontrar meu grupo", to: "/grupos" },
  title: "Tudo pronto! 🙏",
  body: "Agora é com você. Comece encontrando o seu Pequeno Grupo mais perto.",
};

function buildSteps(role) {
  if (MEMBER_ROLES.includes(role)) return [INTRO_MEMBER, ...MEMBER_STEPS, MEMBER_CTA];
  return [INTRO_LEADER, ...LEADER_STEPS, LEADER_CTA];
}

export function WelcomeTour() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);

  const steps = user ? buildSteps(user.role) : [];
  const total = steps.length;

  // abre sozinho no primeiro acesso
  useEffect(() => {
    if (user && user.tour_seen !== true) {
      setIdx(0);
      setOpen(true);
    }
  }, [user]);

  // reabrir via botão "?" / menu
  useEffect(() => {
    const onOpen = () => { setIdx(0); setOpen(true); };
    window.addEventListener("open-tour", onOpen);
    return () => window.removeEventListener("open-tour", onOpen);
  }, []);

  const finish = useCallback(async (navTo) => {
    setOpen(false);
    try {
      await api.post("/auth/tour-seen");
      if (user) setUser({ ...user, tour_seen: true });
    } catch { /* ignore */ }
    if (navTo) navigate(navTo);
  }, [user, setUser, navigate]);

  if (!open || !user || total === 0) return null;
  const s = steps[idx];
  const Icon = s.icon || Rocket;
  const isFirst = idx === 0;
  const isLast = idx === total - 1;

  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-3 sm:p-6" data-testid="welcome-tour">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => finish()} />
      <div className="relative w-full max-w-md rounded-3xl border border-white/10 bg-card shadow-2xl overflow-hidden slide-up" data-testid="tour-card">
        {/* header */}
        <div className="flex items-center justify-between px-5 pt-5">
          <div className="flex items-center gap-2">
            <Emblem className="h-7 w-7" />
            <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Tour do app</span>
          </div>
          <button onClick={() => finish()} data-testid="tour-skip" className="text-sm text-muted-foreground hover:text-foreground px-2 py-1">
            Pular
          </button>
        </div>

        {/* body */}
        <div className="px-6 pb-2 pt-4 text-center">
          <div className={`mx-auto mb-4 inline-flex items-center justify-center rounded-2xl ${s.cover ? "h-20 w-20 bg-gradient-to-br from-accent/20 to-primary/20" : "h-16 w-16 bg-primary/10"}`}>
            <Icon className={`${s.cover ? "h-9 w-9" : "h-8 w-8"} text-primary`} />
          </div>
          <h3 className="font-display font-extrabold tracking-tight text-2xl mb-2" data-testid="tour-title">{s.title}</h3>
          {!s.cover && s.where && (
            <div className="inline-block rounded-full bg-secondary px-3 py-1 text-xs font-medium text-muted-foreground mb-3" data-testid="tour-where">{s.where}</div>
          )}
          <p className="text-base text-muted-foreground leading-relaxed">{s.body}</p>
          {s.cta && (
            <button onClick={() => finish(s.cta.to)} data-testid="tour-cta"
              className="mt-5 inline-flex items-center justify-center gap-2 rounded-full bg-primary px-6 py-3 text-base font-semibold text-primary-foreground hover:opacity-90 transition-opacity w-full">
              {s.cta.label} <ChevronRight className="h-5 w-5" />
            </button>
          )}
        </div>

        {/* progress dots */}
        <div className="flex justify-center gap-1.5 px-6 py-4">
          {steps.map((_, i) => (
            <span key={i} className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-primary" : "w-1.5 bg-border"}`} />
          ))}
        </div>

        {/* footer nav */}
        <div className="flex items-center justify-between gap-3 border-t border-border px-5 py-4">
          <button onClick={() => setIdx((i) => Math.max(0, i - 1))} disabled={isFirst} data-testid="tour-prev"
            className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-secondary disabled:opacity-30 transition-colors">
            <ChevronLeft className="h-4 w-4" /> Voltar
          </button>
          <span className="text-xs text-muted-foreground tabular-nums" data-testid="tour-progress">Passo {idx + 1} de {total}</span>
          {isLast ? (
            <button onClick={() => finish()} data-testid="tour-finish"
              className="inline-flex items-center gap-1.5 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90 transition-opacity">
              Concluir
            </button>
          ) : (
            <button onClick={() => setIdx((i) => Math.min(total - 1, i + 1))} data-testid="tour-next"
              className="inline-flex items-center gap-1.5 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90 transition-opacity">
              Avançar <ChevronRight className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
      <style>{`
        @keyframes tourUp { from { opacity: 0; transform: translateY(24px) scale(.98); } to { opacity: 1; transform: translateY(0) scale(1); } }
        .slide-up { animation: tourUp .32s cubic-bezier(.2,.7,.2,1) both; }
      `}</style>
    </div>
  );
}

export function openTour() {
  window.dispatchEvent(new Event("open-tour"));
}
